import { useState } from "react";
import { ApplicantCollection } from "../../../components/Applicant/ApplicantCollection";
import { RadioButtonGroup } from "../../../components/Decision/RadioButtonGroup";
import { FormTextArea } from "../../../components/Input/FormTextArea";
import {
  getRolesFromSessionStorage,
  isReaderOnly
} from "../../../utils/userHelper";
import { CommonUserTaskNew } from "../CommonUserTaskNew";
import ManualCheckFinalFi from "../manual-check-final-fi";
import ManualCheckFinalSe from "../manual-check-final-se";
import * as Styles from "../styles";
import type { ComponentConfig } from "../types";
import {
  ApplicantDetailsLabel,
  ApplicantDetailsWrapper,
  FlexAlignCenter,
  LabelBold
} from "./styles";

const COMMON_PROCESS_CONFIG = ["NO", "SE", "FI"];

const ManualCheckFinal = (props: any) => {
  // The Swedish loan form (ManualCheckFinalSe) is loan-specific (autogiro, disbursement
  // account, insurance). Card cases must fall through to the simple decision+comment form.
  const isCard = props?.flow?.data?.product === "card";
  const country = props?.flow?.data?.country;

  if (!isCard && COMMON_PROCESS_CONFIG.includes(country)) {
    if (country === "FI") {
      return <ManualCheckFinalFi {...props} />;
    }
    return <ManualCheckFinalSe {...props} />;
  }

  // Simple decision+comment form (card cases and non-common-process countries).
  // Kept as its own component so the hooks below are never called after the
  // early returns above (rules of hooks).
  return <ManualCheckFinalDefault {...props} />;
};

const ManualCheckFinalDefault = (props: any) => {
  const formConfig: ComponentConfig[] = [];
  const data = props.flow?.data;
  const applicant = data?.agentApiApplication?.applicant;
  const coapplicant = data?.agentApiApplication?.coapplicant;

  const userRoles = getRolesFromSessionStorage();
  const userIsReader = isReaderOnly(userRoles);

  const [decision, setDecision] = useState(props?.task?.data?.decision ?? null);
  const [comment, setComment] = useState(props?.task?.data?.comment ?? null);

  const decisionErrorMessage = "Please select a decision.";
  const noErrorMessage = "";
  const [errorMessages, setErrorMessages] = useState<string>(noErrorMessage);

  const handleDecisionChange = (value: any) => {
    const decisionValue = value === "true";
    setDecision(decisionValue);
  };

  const handleNoteChange = (value: any) => {
    setComment(value);
  };

  const handleSave = async () => {
    const formData = {
      decision,
      comment
    };
    props.save(formData, (error: any) => console.error({ error }), true);
  };

  const handleComplete = async () => {
    if (decision === null) {
      setErrorMessages(decisionErrorMessage);
      return;
    }

    const formData = {
      decision,
      comment
    };
    props.complete(
      formData,
      () => {},
      (e: any) => {
        setErrorMessages(e?.detail);
      }
    );
  };

  return (
    <>
      {errorMessages !== noErrorMessage && (
        <Styles.ErrorMessageBox>
          <FlexAlignCenter>
            <LabelBold>{errorMessages}</LabelBold>
          </FlexAlignCenter>
        </Styles.ErrorMessageBox>
      )}
      <ApplicantDetailsWrapper>
        <ApplicantDetailsLabel>Applicant details</ApplicantDetailsLabel>
        <ApplicantCollection {...applicant} />
        {coapplicant && <ApplicantCollection {...coapplicant} />}
      </ApplicantDetailsWrapper>
      <RadioButtonGroup
        title="Decision"
        id="approved"
        radioButtonValue={decision}
        options={[
          { label: "Approve application", value: true },
          { label: "Reject application", value: false }
        ]}
        onChange={(e: any) => handleDecisionChange(e)}
      />
      <br />
      <FormTextArea
        label="Internal comment"
        name="internal-comment"
        onChange={(e: any) => handleNoteChange(e)}
        defaultValue={comment}
        placeholder="Other applicant information..."
      />
      <Styles.ContentFlexWrapper>
        <CommonUserTaskNew
          handleComplete={() => {
            handleComplete();
          }}
          handleSave={() => {
            handleSave();
          }}
          formConfig={formConfig}
          overrideMasonryColumns={3}
          disablePrimary={userIsReader || props.canComplete === false}
          disableSecondary={userIsReader || props.canSave === false}
        />
      </Styles.ContentFlexWrapper>
    </>
  );
};

export default ManualCheckFinal;
